/**
 * groupConversationsByProject — deterministic grouping for the sidebar.
 *
 * Contract:
 *   - Every conversation lands in exactly one group, keyed by `project_id`.
 *   - Conversations without a project go into the synthetic `null` group
 *     ("Senza progetto") which is always rendered LAST regardless of how
 *     many projects exist.
 *   - Project order follows the order the caller passes in (the projects
 *     hook is responsible for sorting by `position` / recency).
 *   - Inside each group, conversations keep the order from the input list
 *     (already recency-descending via `useConversationsQuery`).
 *
 * Pure / no-React. Easy to unit-test if needed later.
 */

import type { Project } from "@ando/core/api";
import type { ChatConversation } from "../state/types";

/** Sentinel key for conversations without a `project_id`. */
export const UNASSIGNED_PROJECT_KEY = "_unassigned" as const;

/** Group with the parent project (null for unassigned) + member chats. */
export interface ConversationGroup {
  /** Stable key for React lists + collapsed-state lookup. */
  key: string;
  /** Project metadata, or null for the unassigned bucket. */
  project: Project | null;
  conversations: ChatConversation[];
}

/**
 * Extract a stable project_id from a conversation entry. The BE migration
 * adding `project_id` to the conversation row is rolling out — until every
 * row carries it, we accept the field as optional and fall back to null
 * (unassigned bucket).
 */
function readProjectId(conv: ChatConversation): string | null {
  const maybe = (conv as { project_id?: string | null }).project_id;
  return typeof maybe === "string" && maybe.length > 0 ? maybe : null;
}

export function groupConversationsByProject(
  conversations: readonly ChatConversation[],
  projects: readonly Project[],
): ConversationGroup[] {
  // Bucket by project id. Map preserves insertion order which is useful for
  // the "unassigned LAST" rule and stable rendering.
  const buckets = new Map<string | null, ChatConversation[]>();
  for (const c of conversations) {
    const key = readProjectId(c);
    const arr = buckets.get(key);
    if (arr) {
      arr.push(c);
    } else {
      buckets.set(key, [c]);
    }
  }

  // Emit groups in the order the caller passed projects in. We render
  // empty project groups too — the user just created the project from
  // the pill and needs visual confirmation that it exists *before* any
  // conversation lives inside. The header doubles as a drop target /
  // discovery affordance ("here is where new chats will land"), which
  // a hidden empty header would silently break.
  const groups: ConversationGroup[] = [];
  for (const project of projects) {
    const members = buckets.get(project.project_id) ?? [];
    groups.push({ key: project.project_id, project, conversations: members });
  }

  // Unassigned bucket — always last, only if non-empty.
  const unassigned = buckets.get(null);
  if (unassigned && unassigned.length > 0) {
    groups.push({
      key: UNASSIGNED_PROJECT_KEY,
      project: null,
      conversations: unassigned,
    });
  }

  return groups;
}
